import { useEffect, useRef, useState } from 'react';

export function useElementOnScreen(
	elementSelector: string,
	parentSelector?: string,
	options?: IntersectionObserverInit,
) {
	const [isIntersecting, setIntersecting] = useState(true); // Start with true to avoid premature fixed header
	const observerRef = useRef<IntersectionObserver | null>(null);

	useEffect(() => {
		if (typeof window === 'undefined' || !window.document) {
			setIntersecting(false);
			return;
		}

		// Don't reset to false immediately to avoid flashing
		observerRef.current?.disconnect();

		observerRef.current = new IntersectionObserver(
			([entry]) => setIntersecting(entry?.isIntersecting || false),
			options,
		);

		const observeElement = () => {
			const element = document.querySelector(elementSelector);
			if (element) {
				observerRef.current?.observe(element);
				return true;
			}
			return false;
		};

		// Try to observe, or watch for changes
		if (!observeElement()) {
			const mutationObserver = new MutationObserver(() => {
				if (observeElement()) {
					mutationObserver.disconnect();
				}
			});

			const targetContainer =
				(parentSelector && document.querySelector(parentSelector)) ||
				document.body;

			if (!targetContainer) {
				// Avoid passing null!
				return;
			}

			mutationObserver.observe(targetContainer, {
				childList: true,
				subtree: true,
			});

			return () => {
				mutationObserver.disconnect();
				observerRef.current?.disconnect();
			};
		}

		return () => observerRef.current?.disconnect();
	}, [elementSelector, parentSelector, options]);
	// Notice: optionally only depend on options' stable props, or force user to memoize

	return isIntersecting;
}
